import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { 
  ModuleRegistry,
  ColDef, 
  ValueFormatterParams, 
  ValueGetterParams, 
  ICellRendererParams,
  GridReadyEvent,
  GridApi,
  CellValueChangedEvent,
  SelectionChangedEvent
} from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { Loader2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Pencil, PencilOff } from 'lucide-react';
import { toast } from 'sonner';
import { priceBookItemService, type PriceBookItem } from '@/services';
import { profileService } from '@/services';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { usePagination } from '@/hooks/usePagination';
import { useAuth } from '@/contexts/AuthContext';
import type { ServiceContext } from '@prolarus/shared';

// Register AG Grid modules
ModuleRegistry.registerModules([AllEnterpriseModule]);

// Helper functions
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`;
};

const calculateDiscountedPrice = (price: number, discount: number) => {
  return price * (1 - discount / 100);
};

interface PriceBookItemsAGGridProps {
  priceBookId: string;
  onSelectionChanged?: (rows: PriceBookItem[]) => void;
  onGridApiReady?: (api: GridApi<PriceBookItem>) => void;
  onSearchChange?: (search: string) => void;
  onTotalCountChange?: (count: number) => void;
  toolbarActions?: React.ReactNode;
}

export default function PriceBookItemsAGGrid({ priceBookId, onSelectionChanged, onGridApiReady, onSearchChange, onTotalCountChange, toolbarActions }: PriceBookItemsAGGridProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const gridApiRef = useRef<GridApi<PriceBookItem> | null>(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  // Use pagination hook with localStorage persistence
  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({
    defaultPageSize: 25,
    storageKey: 'price-book-items-ag',
    persistToUrl: false, // Avoid URL conflicts on detail page
  });

  // Profile query for service context
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Service context helper
  const getServiceContext = useCallback((): ServiceContext => ({
    companyId: profile?.company_id!,
    userId: user?.id,
  }), [profile?.company_id, user?.id]);

  // If the user switches Price Books, reset to the first page.
  // This prevents an out-of-range offset from localStorage from causing a 416.
  useEffect(() => {
    if (!priceBookId) return;
    setPageIndex(0);
  }, [priceBookId, setPageIndex]);

  const isRangeNotSatisfiable = (error: unknown): boolean => {
    const e = error as any;
    return e?.status === 416 || e?.statusCode === 416 || e?.code === 416 || e?.code === '416';
  };

  // Fetch paginated items
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['price-book-items-ag', priceBookId, pageIndex, pageSize, debouncedSearch],
    queryFn: async () => {
      const result = await priceBookItemService.listByPriceBookPaginated(
        priceBookId,
        { page: pageIndex, pageSize },
        { search: debouncedSearch }
      );

      if (result.error) {
        // If we ever end up out-of-range (typically due to stale localStorage state),
        // jump back to the first page and let React Query refetch.
        if (isRangeNotSatisfiable(result.error)) {
          setPageIndex(0);
          return { items: [], totalCount: 0 };
        }
        throw result.error;
      }

      return { items: result.data, totalCount: result.totalCount };
    },
    enabled: !!priceBookId,
    placeholderData: (previousData) => previousData,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Clamp pageIndex when totalCount changes (fixes 416 Range Not Satisfiable error)
  useEffect(() => {
    if (data?.totalCount !== undefined && data.totalCount > 0) {
      const maxValidPage = Math.ceil(data.totalCount / pageSize) - 1;
      if (pageIndex > maxValidPage) {
        setPageIndex(Math.max(0, maxValidPage));
      }
    } else if (data?.totalCount === 0 && pageIndex !== 0) {
      setPageIndex(0);
    }
  }, [data?.totalCount, pageIndex, pageSize, setPageIndex]);

  // Cell value changed handler for inline editing
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent<PriceBookItem>) => {
    const { data: rowData, colDef, oldValue, newValue } = event;
    if (!rowData || oldValue === newValue) return;

    const columnId = colDef.field as keyof PriceBookItem;
    const snapshot = { [columnId]: oldValue };
    const updateData = { [columnId]: newValue };

    // Optimistic update already applied by AG Grid
    try {
      const result = await priceBookItemService.update(
        rowData.id,
        updateData,
        snapshot,
        priceBookId,
        getServiceContext()
      );
      
      if (result.error) throw result.error;
      // Silent success - no toast for inline edits
    } catch (error) {
      // Rollback by refetching data
      queryClient.invalidateQueries({ queryKey: ['price-book-items-ag', priceBookId] });
      toast.error('Failed to update item');
      console.error(error);
    }
  }, [priceBookId, getServiceContext, queryClient]);

  // Column definitions with conditional editing
  const columnDefs = useMemo<ColDef<PriceBookItem>[]>(() => [
    { 
      field: 'sequence', 
      headerName: 'Seq', 
      width: 80, 
      sortable: true,
      suppressSizeToFit: true,
      editable: false, // Never editable
    },
    { 
      field: 'itemcode', 
      headerName: 'Item Code', 
      sortable: true,
      minWidth: 120,
      editable: isEditMode,
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 1, 
      sortable: true,
      minWidth: 200,
      editable: isEditMode,
    },
    { 
      field: 'price', 
      headerName: 'Price',
      valueFormatter: (params: ValueFormatterParams<PriceBookItem, number>) => 
        params.value != null ? formatCurrency(params.value) : '',
      type: 'rightAligned',
      sortable: true,
      width: 120,
      editable: isEditMode,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { precision: 4 },
    },
    { 
      field: 'unit', 
      headerName: 'Unit of Measure', 
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<PriceBookItem, string | null>) => 
        params.value || '—',
      minWidth: 130,
      editable: false, // Would require custom dropdown editor - out of scope
    },
    { 
      field: 'discount', 
      headerName: 'Disc%',
      valueFormatter: (params: ValueFormatterParams<PriceBookItem, number>) => 
        params.value != null ? formatPercent(params.value) : '',
      type: 'rightAligned',
      sortable: true,
      width: 100,
      editable: isEditMode,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { precision: 2, min: 0, max: 100 },
    },
    { 
      headerName: "Disc'd Price",
      valueGetter: (params: ValueGetterParams<PriceBookItem>) => {
        if (!params.data) return null;
        return calculateDiscountedPrice(params.data.price, params.data.discount);
      },
      valueFormatter: (params: ValueFormatterParams<PriceBookItem, number>) => 
        params.value != null ? formatCurrency(params.value) : '',
      type: 'rightAligned',
      sortable: false,
      width: 120,
      editable: false, // Calculated field
    },
    { 
      field: 'is_active', 
      headerName: 'Active',
      cellRenderer: (params: ICellRendererParams<PriceBookItem, boolean>) => 
        params.value ? '✓' : '✗',
      cellClass: 'text-center',
      headerClass: 'ag-header-center',
      width: 80,
      sortable: true,
      suppressSizeToFit: true,
      editable: isEditMode,
      cellEditor: 'agCheckboxCellEditor',
    },
  ], [isEditMode]);

  // Default column settings
  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
  }), []);

  const onGridReady = useCallback((params: GridReadyEvent<PriceBookItem>) => {
    gridApiRef.current = params.api;
    onGridApiReady?.(params.api);
  }, [onGridApiReady]);

  // Notify parent of search changes
  useEffect(() => {
    onSearchChange?.(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  // Notify parent of total count changes
  useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount, onTotalCountChange]);

  // Pagination handlers
  const goToFirstPage = () => setPageIndex(0);
  const goToPreviousPage = () => setPageIndex(Math.max(0, pageIndex - 1));
  const goToNextPage = () => setPageIndex(Math.min(totalPages - 1, pageIndex + 1));
  const goToLastPage = () => setPageIndex(totalPages - 1);

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value, 10));
  };

  // Calculate displayed range
  const startRow = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, totalCount);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-card border border-border rounded-lg">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Toolbar with search and edit toggle */}
      <div className="p-4 border-b border-border flex items-center justify-between gap-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search item code, description, or unit..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          {toolbarActions}
          <Button
            variant={isEditMode ? "default" : "outline"}
            size="icon"
            onClick={() => setIsEditMode(!isEditMode)}
            title={isEditMode ? "Switch to view mode" : "Enable inline editing"}
          >
            {isEditMode ? <PencilOff className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div 
        className="ag-theme-quartz relative" 
        style={{ height: 'calc(100vh - 480px)', minHeight: '300px', width: '100%' }}
      >
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        <AgGridReact<PriceBookItem>
          rowData={items}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
          selectionColumnDef={{ pinned: 'left' }}
          onSelectionChanged={(event: SelectionChangedEvent<PriceBookItem>) => {
            onSelectionChanged?.(event.api.getSelectedRows());
          }}
          suppressCellFocus={!isEditMode}
          domLayout="normal"
          onGridReady={onGridReady}
          onCellValueChanged={handleCellValueChanged}
          stopEditingWhenCellsLoseFocus={true}
          singleClickEdit={isEditMode}
        />
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {totalCount === 0 
              ? 'No items' 
              : `${startRow}-${endRow} of ${totalCount}`}
          </span>
          
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToFirstPage}
              disabled={pageIndex === 0}
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToPreviousPage}
              disabled={pageIndex === 0}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToNextPage}
              disabled={pageIndex >= totalPages - 1}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToLastPage}
              disabled={pageIndex >= totalPages - 1}
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
